/**
 * Auth context — session user + login/logout, backed by the cookie session.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserDto } from '@vidply/shared';
import { api } from './api';

interface AuthContextValue {
  user: UserDto | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  refresh: async () => undefined,
  logout: async () => undefined,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me().catch(() => null), // 401 = signed out, not an error
    staleTime: 5 * 60_000,
    retry: false,
  });

  const value: AuthContextValue = {
    user: data?.user ?? null,
    loading: isLoading,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    logout: async () => {
      await api.logout().catch(() => undefined);
      queryClient.setQueryData(['me'], null);
      await queryClient.invalidateQueries();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
